import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Activity,
  AlertCircle,
  Archive,
  ArrowRight,
  CheckCircle2,
  Download,
  Eye,
  FileCheck2,
  FileText,
  FolderOpen,
  MessageSquareText,
  Plus,
  Scale,
  Trash2,
  UserRound,
} from 'lucide-react';
import LaborOperationsLayout from '../../../components/labor/LaborOperationsLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';
import {
  addLaborCaseMissingItem,
  addLaborCaseNote,
  archiveLaborCase,
  assignLaborCase,
  getAdminLaborCaseById,
  getLaborAdminAccess,
  getVisibleAttachments,
  laborAdminStatuses,
  laborAssignees,
  removeLaborAttachment,
  updateLaborCaseMissingStatus,
  updateLaborCaseStatus,
} from '../../../features/labor-intake/laborIntakeAdminService';

const dash = (value) => (value === undefined || value === null || value === '' ? '-' : value);
const formatDate = (value) =>
  value ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(value)) : '-';
const dateOnly = (value) => value ? new Intl.DateTimeFormat('ar-SA', { dateStyle: 'medium' }).format(new Date(value)) : '-';
const fileSize = (size) => (size ? `${(size / 1024 / 1024).toFixed(2)} MB` : '-');
const fullName = (client) =>
  client?.fullName || [client?.firstName, client?.fatherName, client?.grandfatherName, client?.familyName].filter(Boolean).join(' ') || '-';
const companyName = (company) => company?.tradeName || company?.legalName || '-';

const approvalLabels = { approved: 'مصادقة كاملة', partial: 'مصادقة جزئية', rejected: 'غير مصادق' };
const missingStatusLabels = { required: 'مطلوب', uploaded: 'تم الرفع', reviewed: 'تمت المراجعة' };

const workflow = [
  { id: 'received', label: 'الاستلام', icon: FolderOpen },
  { id: 'data', label: 'البيانات', icon: UserRound },
  { id: 'documents', label: 'المستندات', icon: FileText },
  { id: 'review', label: 'المراجعة', icon: Scale },
  { id: 'assignment', label: 'الإسناد', icon: FileCheck2 },
  { id: 'closed', label: 'الإغلاق', icon: CheckCircle2 },
];

const workflowIndex = {
  new: 0,
  incomplete: 1,
  missing_required: 2,
  waiting_client: 2,
  under_review: 3,
  complete: 3,
  preliminarily_accepted: 4,
  assigned_lawyer: 4,
  rejected: 5,
  archived: 5,
};

const DataRows = ({ fields }) => (
  <dl className="grid gap-x-8 md:grid-cols-2">
    {fields.map(([label, value]) => (
      <div key={label} className="flex min-h-[68px] items-center justify-between gap-4 border-b border-[#ece8e0] py-4 transition hover:bg-[#faf8f3]">
        <dt className="text-[14px] font-medium text-[#697483]">{label}</dt>
        <dd className="max-w-[58%] break-words text-left text-[15px] font-bold text-[#293a50]">{dash(value)}</dd>
      </div>
    ))}
  </dl>
);

const Panel = ({ title, children, action }) => (
  <section className="lo-surface overflow-hidden rounded-[16px]">
    <header className="flex items-center justify-between border-b border-[#e7e2d9] px-6 py-5">
      <h3 className="legal-title text-[18px] font-bold text-[#293a50]">{title}</h3>
      {action}
    </header>
    <div className="p-6">{children}</div>
  </section>
);

const LaborIntakeAdminDetailsPage = () => {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAdminAuth();
  const access = getLaborAdminAccess(user);
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [missingForm, setMissingForm] = useState({ title: '', description: '' });
  const [noteText, setNoteText] = useState('');

  useEffect(() => {
    getAdminLaborCaseById(caseId, user, token)
      .then(setItem)
      .finally(() => setLoading(false));
  }, [caseId, token, user]);

  const refreshWith = async (updateRequest) => {
    const updated = await updateRequest;
    if (updated) setItem(updated);
  };
  const tabs = useMemo(() => {
    if (access.role === 'intake') return [
      { id: 'overview', label: 'الملخص' },
      { id: 'client', label: 'العميل' },
      { id: 'company', label: 'المنشأة' },
      { id: 'missing', label: 'النواقص' },
    ];
    const available = [
      { id: 'overview', label: 'الملخص' },
      { id: 'client', label: 'العميل' },
      { id: 'company', label: 'المنشأة' },
      { id: 'employment', label: 'العلاقة والأجور' },
      { id: 'termination', label: 'الانتهاء والمطالبات' },
      { id: 'facts', label: 'ملخص الوقائع' },
      { id: 'attachments', label: 'المرفقات' },
      { id: 'missing', label: 'النواقص' },
      { id: 'notes', label: 'ملاحظات داخلية' },
    ];
    return access.canSeeSensitive ? available : available.filter((tab) => tab.id !== 'notes');
  }, [access]);

  if (loading) {
    return (
      <LaborOperationsLayout>
        <div className="flex h-[calc(100vh-66px)] items-center justify-center text-sm text-[#697483]" dir="rtl">جارٍ تحميل الملف...</div>
      </LaborOperationsLayout>
    );
  }

  if (!item) {
    return (
      <LaborOperationsLayout>
        <div className="flex h-[calc(100vh-66px)] flex-col items-center justify-center" dir="rtl">
          <p className="text-lg font-bold text-[#273950]">الملف غير متاح</p>
          <button className="mt-5 rounded-xl bg-[#33455d] px-5 py-2.5 text-sm font-bold text-white shadow-[0_10px_22px_rgba(39,57,80,0.14)]" onClick={() => navigate('/admin/labor-intake')}>العودة إلى الملفات</button>
        </div>
      </LaborOperationsLayout>
    );
  }

  const client = item.client || {};
  const company = item.company || {};
  const contract = item.contract || {};
  const compensation = item.compensation || {};
  const attachments = getVisibleAttachments(item);
  const activities = [...item.adminData.activity, ...(item.timeline || []).map((event) => ({ ...event, actor: 'بوابة العميل' }))]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const currentWorkflow = workflowIndex[item.adminData.status] ?? 0;
  const pendingMissing = item.adminData.missingItems.filter((missing) => missing.status === 'required').length;
  const hasContract = attachments.some((file) => `${file.typeKey} ${file.typeLabel}`.includes('contract') || `${file.typeLabel}`.includes('عقد'));
  const hasBank = attachments.some((file) => `${file.typeKey} ${file.typeLabel}`.includes('bank') || `${file.typeLabel}`.includes('كشف حساب'));
  const hasGosi = attachments.some((file) => `${file.typeKey} ${file.typeLabel}`.includes('gosi') || `${file.typeLabel}`.includes('التأمينات'));
  const updateStatus = (status) => refreshWith(updateLaborCaseStatus(item.id, status, user, token));

  const centerContent = {
    overview: (
      <Panel title="بيانات الملف الأساسية">
        <DataRows fields={access.role === 'intake' ? [
          ['الرقم المرجعي', item.referenceNumber],
          ['اسم العميل', fullName(client)],
          ['رقم الجوال', client.mobile],
          ['البريد الإلكتروني', client.email],
          ['اسم المنشأة', companyName(company)],
          ['الحالة', item.statusInfo.label],
        ] : [
          ['الرقم المرجعي', item.referenceNumber],
          ['اسم العميل', fullName(client)],
          ['رقم الجوال', client.mobile],
          ['البريد الإلكتروني', client.email],
          ['اسم المنشأة', companyName(company)],
          ['الحالة', item.statusInfo.label],
          ['تاريخ التقديم', formatDate(item.submittedAt)],
          ['آخر تحديث', formatDate(item.lastUpdatedAt)],
          ['المسؤول', item.assigneeName],
          ['مصادقة العميل', approvalLabels[item.summary?.approvalStatus] || 'غير مصادق'],
        ]} />
      </Panel>
    ),
    client: (
      <Panel title="بيانات العميل">
        <DataRows fields={access.role === 'intake' ? [
          ['الاسم الكامل', fullName(client)], ['رقم الجوال', client.mobile], ['البريد الإلكتروني', client.email], ['المدينة', client.city],
        ] : [
          ['الاسم الكامل', fullName(client)], ['نوع الهوية', client.identityType], ['رقم الهوية', client.nationalId],
          ['رقم الجوال', client.mobile], ['البريد الإلكتروني', client.email], ['الجنسية', client.nationality],
          ['المدينة', client.city], ['العنوان الوطني', [client.district, client.street, client.buildingNumber, client.postalCode].filter(Boolean).join(' - ')],
          ['المسمى الوظيفي', client.jobTitle], ['على رأس العمل', client.currentEmploymentStatus], ['آخر يوم عمل', dateOnly(client.lastWorkingDay)],
        ]} />
      </Panel>
    ),
    company: (
      <Panel title="بيانات المنشأة">
        <DataRows fields={access.role === 'intake' ? [
          ['اسم المنشأة', companyName(company)], ['هاتف المنشأة', company.phone], ['البريد الرسمي', company.email],
        ] : [
          ['اسم المنشأة', companyName(company)], ['نوع المنشأة', company.establishmentType],
          ['السجل التجاري / الرقم الموحد', company.officialRecords?.[0]?.number || company.licenseNumber],
          ['بيانات الترخيص', [company.licenseNumber, company.licenseIssuedBy, company.licenseIssueDate].filter(Boolean).join(' - ')],
          ['المقر الرئيسي', company.headOffice], ['هاتف المنشأة', company.phone], ['البريد الرسمي', company.email],
          ['الموقع الإلكتروني', company.website], ['المسؤول المباشر', company.directSupervisor],
          ['مسؤول الموارد البشرية', company.hrContact], ['القطاع', company.sector],
        ]} />
      </Panel>
    ),
    employment: (
      <Panel title="العلاقة والأجور">
        <DataRows fields={[
          ['نوع العلاقة أو الاتفاق', contract.contractType], ['تاريخ توقيع العقد', dateOnly(contract.contractDate)],
          ['تاريخ مباشرة العمل', dateOnly(contract.startDate)], ['العقد موثق في قوى', contract.qiwaContract],
          ['نوع العقد', contract.contractDurationType], ['طريقة استلام الأجر', contract.salaryReceiptMethod || compensation.paymentMethod],
          ['الأجر الأساسي', compensation.basicSalary], ['الأجر الشامل', compensation.totalSalary],
          ['البدلات', [compensation.housingAllowance, compensation.transportAllowance, compensation.mobilityAllowance].filter(Boolean).join('، ')],
          ['العمولات', compensation.commissionsValue], ['الحوافز', compensation.incentivesValue], ['التأمين الطبي', compensation.medicalInsurance],
          ['مطابقة الأجر للعقد', compensation.contractWageMatchesActual], ['مطابقة الأجر للتأمينات', compensation.gosiWageMatchesActual],
          ['الاختلافات المذكورة', compensation.wageDifferenceReason || compensation.duesDetails],
        ]} />
      </Panel>
    ),
    termination: (
      <Panel title="انتهاء العلاقة والمطالبات">
        <DataRows fields={[
          ['حالة العلاقة', client.currentEmploymentStatus === 'لا' ? 'منتهية' : 'قائمة'], ['آخر يوم عمل', dateOnly(client.lastWorkingDay || contract.lastDay)],
          ['سبب الانتهاء', contract.endReason], ['تم إشعار العميل', contract.noticeProvided], ['طريقة الإشعار', contract.noticeDeliveryMethod],
          ['تاريخ الإشعار', dateOnly(contract.noticeDate)], ['وقّع على مخالصة', contract.releaseReceived], ['تاريخ المخالصة', dateOnly(contract.releaseDate)],
          ['استلم شهادة خبرة', contract.experienceCertificateReceived], ['تم إخلاء طرفه', contract.clearanceCompleted],
        ]} />
        <div className="mt-6">
          <p className="mb-4 text-[14px] font-bold text-[#697483]">المطالبات المذكورة</p>
          <div className="flex flex-wrap gap-2">
            {(compensation.claims || []).length ? compensation.claims.map((claim) => (
              <span key={claim} className="rounded-lg bg-[#f0eee8] px-3 py-2 text-[14px] font-bold text-[#293a50]">{claim}</span>
            )) : <span className="text-sm text-slate-400">لا توجد مطالبات محددة.</span>}
          </div>
        </div>
      </Panel>
    ),
    facts: (
      <Panel title="ملخص الوقائع">
        <div className="mb-5 inline-flex rounded-lg bg-[#eee8dd] px-3 py-2 text-[14px] font-bold text-[#806f58]">
          {approvalLabels[item.summary?.approvalStatus] || 'غير مصادق'}
        </div>
        {item.summary?.approvalStatus === 'partial' && (
          <div className="mb-5 rounded-lg border border-[#e4d7bb] bg-[#fcfaf4] p-4 text-sm leading-7 text-[#5c4b2d]">{dash(item.summary.approvalNote)}</div>
        )}
        <p className="text-[15px] leading-9 text-[#556171]">{dash(item.narrative?.fullStory || contract.problemSummary || contract.endStory)}</p>
        <p className="mt-6 border-t border-[#ece8e0] pt-5 text-[14px] text-[#697483]">ملخص بيانات ووقائع صادق عليها العميل، وليس رأياً قانونياً.</p>
      </Panel>
    ),
    attachments: (
      <Panel title="المرفقات">
        <div className="overflow-x-auto">
          <table className="lo-table min-w-[690px] w-full">
            <thead className="text-right">
              <tr>{['النوع', 'اسم الملف', 'تاريخ الرفع', 'الحجم', ''].map((header) => <th key={header} className="border-b border-[#f0e8dc] px-3 py-3">{header}</th>)}</tr>
            </thead>
            <tbody>
              {attachments.map((attachment) => (
                <tr key={attachment.id} className="border-b border-[#f1eee8]">
                  <td className="px-3 py-3 font-bold text-[#273950]">{dash(attachment.typeLabel || attachment.displayName)}</td>
                  <td className="px-3 py-3 text-slate-500">{dash(attachment.fileName || attachment.name)}</td>
                  <td className="px-3 py-3 text-xs text-slate-500">{formatDate(attachment.uploadedAt)}</td>
                  <td className="px-3 py-3 text-xs text-slate-500">{fileSize(attachment.size)}</td>
                  <td className="px-3 py-3"><div className="flex gap-1.5">
                    <button title="المعاينة تتطلب تخزين الملف الفعلي" className="p-1.5 text-slate-400"><Eye className="h-4 w-4" /></button>
                    <button title="التنزيل يتطلب تخزين الملف الفعلي" className="p-1.5 text-slate-400"><Download className="h-4 w-4" /></button>
                    {access.canDeleteAttachment && <button onClick={() => refreshWith(removeLaborAttachment(item.id, attachment.id, user, token))} className="p-1.5 text-red-400"><Trash2 className="h-4 w-4" /></button>}
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
          {!attachments.length && <p className="py-12 text-center text-sm text-slate-400">لا توجد مرفقات.</p>}
        </div>
      </Panel>
    ),
    missing: (
      <div className="space-y-4">
        {access.canAddMissing && (
          <Panel title="إضافة نقص">
            <div className="space-y-3">
              <input value={missingForm.title} onChange={(event) => setMissingForm((value) => ({ ...value, title: event.target.value }))} placeholder="عنوان النقص" className="lo-control h-12 w-full rounded-xl px-4 outline-none transition" />
              <input value={missingForm.description} onChange={(event) => setMissingForm((value) => ({ ...value, description: event.target.value }))} placeholder="ما المطلوب من العميل؟" className="lo-control h-12 w-full rounded-xl px-4 outline-none transition" />
              <button disabled={!missingForm.title.trim()} onClick={() => { refreshWith(addLaborCaseMissingItem(item.id, missingForm, user, token)); setMissingForm({ title: '', description: '' }); }} className="inline-flex h-10 items-center gap-2 rounded-xl bg-[#33455d] px-4 text-sm font-bold text-white shadow-[0_9px_20px_rgba(39,57,80,0.13)] disabled:opacity-40">
                <Plus className="h-4 w-4" /> إضافة النقص
              </button>
            </div>
          </Panel>
        )}
        <Panel title="النواقص المطلوبة">
          <div className="divide-y divide-[#f0e8dc]">
            {item.adminData.missingItems.map((missing) => (
              <div key={missing.id} className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                <div>
                  <p className="text-[15px] font-bold text-[#293a50]">{missing.title}</p>
                  <p className="mt-1 text-[14px] text-[#697483]">{dash(missing.description)}</p>
                </div>
                {access.canAddMissing ? (
                  <select value={missing.status} onChange={(event) => refreshWith(updateLaborCaseMissingStatus(item.id, missing.id, event.target.value, user, token))} className="lo-control h-10 rounded-lg px-3 font-bold text-[#293a50]">
                    {Object.entries(missingStatusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                ) : <span className="text-xs font-bold">{missingStatusLabels[missing.status]}</span>}
              </div>
            ))}
            {!item.adminData.missingItems.length && <p className="py-8 text-center text-sm text-slate-400">لا توجد نواقص ظاهرة.</p>}
          </div>
        </Panel>
      </div>
    ),
    notes: (
      <div className="space-y-4">
        {access.canAddNotes && (
          <Panel title="ملاحظة داخلية جديدة">
            <textarea value={noteText} onChange={(event) => setNoteText(event.target.value)} rows={4} placeholder="ملاحظة خاصة بالفريق القانوني..." className="lo-control w-full rounded-xl p-4 outline-none transition" />
            <button disabled={!noteText.trim()} onClick={() => { refreshWith(addLaborCaseNote(item.id, noteText.trim(), user, token)); setNoteText(''); }} className="mt-3 inline-flex h-10 items-center gap-2 rounded-xl bg-[#33455d] px-4 text-sm font-bold text-white shadow-[0_9px_20px_rgba(39,57,80,0.13)] disabled:opacity-40">
              <MessageSquareText className="h-4 w-4" /> حفظ
            </button>
          </Panel>
        )}
        <Panel title="الملاحظات الداخلية">
          <div className="space-y-3">
            {item.adminData.notes.map((note) => (
              <article key={note.id} className="border-b border-[#ece8e0] py-5">
                <div className="flex justify-between text-[13px] font-bold text-[#697483]"><span>{note.author}</span><span>{formatDate(note.createdAt)}</span></div>
                <p className="mt-3 text-[15px] leading-8 text-[#556171]">{note.text}</p>
              </article>
            ))}
            {!item.adminData.notes.length && <p className="py-8 text-center text-sm text-slate-400">لا توجد ملاحظات داخلية.</p>}
          </div>
        </Panel>
      </div>
    ),
  };

  return (
    <LaborOperationsLayout>
      <main className="px-6 pb-12 pt-8 sm:px-10" dir="rtl">
        <header className="relative overflow-hidden px-2 pb-9 pt-4">
          <img src="/images/wazin-mark.png" alt="" aria-hidden="true" className="pointer-events-none absolute -left-10 -top-16 h-60 w-60 object-contain opacity-[0.045] mix-blend-multiply" />
          <button onClick={() => navigate('/admin/labor-intake')} className="relative mb-5 inline-flex items-center gap-2 text-[14px] font-bold text-[#697483] transition hover:text-[#293a50]">
            <ArrowRight className="h-4 w-4" /> ملفات القضايا العمالية
          </button>
          <div className="relative flex flex-wrap items-end justify-between gap-5">
            <div>
              <p className="lo-kicker font-bold">{item.referenceNumber}</p>
              <h1 className="legal-title mt-3 text-[38px] font-bold text-[#293a50]">{fullName(client)}</h1>
              <p className="mt-3 text-[16px] text-[#697483]">{companyName(company)} <span className="mx-3 text-[#d2cbc0]">|</span> {item.assigneeName}</p>
            </div>
            <span className={`rounded-full px-4 py-2 text-[14px] font-bold ${item.statusInfo.tone}`}>{item.statusInfo.label}</span>
          </div>
          <div className="relative mt-9 flex gap-px overflow-x-auto rounded-2xl bg-[#e4dfd6] p-px">
            {workflow.map(({ id, label, icon: Icon }, index) => (
              <div key={id} className={`flex min-w-[126px] flex-1 items-center justify-center gap-2 px-4 py-4 text-[14px] font-bold transition ${index <= currentWorkflow ? 'bg-[#fffdfa] text-[#293a50]' : 'bg-[#f3f1eb] text-[#858d97]'}`}>
                <Icon className={`h-4 w-4 ${index <= currentWorkflow ? 'text-[#948066]' : ''}`} />
                {label}
              </div>
            ))}
          </div>
        </header>

        <div className="mt-7 grid gap-6 xl:grid-cols-[292px_minmax(500px,1fr)_300px]">
          <aside className="order-3 space-y-4 xl:order-1">
            <Panel title="سجل النشاط">
              <div className="space-y-4">
                {activities.slice(0, 8).map((event) => (
                  <div key={event.id} className="relative border-r border-[#eee4d2] pr-5 last:border-transparent">
                    <span className="absolute -right-[5px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-[#948066]" />
                    <p className="text-[14px] font-bold text-[#293a50]">{event.label}</p>
                    <p className="mt-1 text-[13px] leading-6 text-[#697483]">{event.actor} | {formatDate(event.createdAt)}</p>
                  </div>
                ))}
                {!activities.length && <p className="text-xs text-slate-400">لا يوجد نشاط مسجل.</p>}
              </div>
            </Panel>
          </aside>

          <section className="order-1 min-w-0 space-y-4 xl:order-2">
            <nav className="flex gap-1 overflow-x-auto rounded-xl bg-[#eeebe4] p-1.5">
              {tabs.map((tab) => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`whitespace-nowrap rounded-lg px-4 py-3 text-[14px] font-bold transition ${activeTab === tab.id ? 'bg-[#fffdfa] text-[#293a50]' : 'text-[#697483] hover:bg-[#f7f5f0] hover:text-[#293a50]'}`}>
                  {tab.label}
                </button>
              ))}
            </nav>
            {centerContent[activeTab]}
          </section>

          <aside className="order-2 space-y-4 xl:order-3">
            {access.role !== 'intake' && (
              <Panel title="مؤشرات الملف">
                <div className="mb-6 rounded-xl bg-[#f1eee8] p-4">
                  <div className="mb-3 flex items-center justify-between text-[14px] font-bold text-[#293a50]"><span>اكتمال الملف</span><span>{item.completion}%</span></div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-[#ece5da]">
                    <div className="h-full rounded-full bg-[#948066]" style={{ width: `${item.completion}%` }} />
                  </div>
                </div>
                <div className="space-y-4 text-[14px]">
                  {[['المرفقات', `${attachments.length} مستند`], ['النواقص', `${pendingMissing} مطلوب`], ['العقد', hasContract ? 'متوفر' : 'غير متوفر'], ['كشف الحساب', hasBank ? 'متوفر' : 'غير متوفر'], ['شهادة المدد والأجور', hasGosi ? 'متوفرة' : 'غير متوفرة']].map(([label, value]) => (
                    <div key={label} className="flex justify-between gap-3"><span className="text-[#978d7d]">{label}</span><span className="font-bold text-[#273950]">{value}</span></div>
                  ))}
                </div>
                <p className="mt-6 border-t border-[#ece8e0] pt-4 text-[13px] leading-6 text-[#697483]">مؤشرات تنظيم واكتمال داخلي فقط.</p>
              </Panel>
            )}
            {(access.canEditStatus || access.canAssign || access.canArchive) && (
              <Panel title="إجراءات الملف">
                <div className="space-y-2.5">
                  {access.canEditStatus && (
                    <select value={item.adminData.status} onChange={(event) => updateStatus(event.target.value)} className="lo-control h-12 w-full rounded-xl px-4 font-bold text-[#293a50]">
                      {laborAdminStatuses.map((status) => <option key={status.value} value={status.value}>{status.label}</option>)}
                    </select>
                  )}
                  {access.canAssign && (
                    <select value={item.adminData.assigneeId} onChange={(event) => refreshWith(assignLaborCase(item.id, event.target.value, user, token))} className="lo-control h-12 w-full rounded-xl px-4 font-bold text-[#293a50]">
                      <option value="">إسناد المسؤول</option>
                      {laborAssignees.map((assignee) => <option key={assignee.id} value={assignee.id}>{assignee.name}</option>)}
                    </select>
                  )}
                  {access.canArchive && <button onClick={() => refreshWith(archiveLaborCase(item.id, user, token))} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-[#f0eee8] text-[14px] font-bold text-[#697483] transition hover:bg-[#e8e4dc] hover:text-[#293a50]"><Archive className="h-4 w-4" /> أرشفة الملف</button>}
                </div>
              </Panel>
            )}
          </aside>
        </div>
      </main>
    </LaborOperationsLayout>
  );
};

export default LaborIntakeAdminDetailsPage;
